import { BlockadeTypeId, ExaminersList, ExtendedExaminersList, MenuItemsConditions } from './ExaminersStructure.types';
import { DashboardTile } from '../../pages/Dashboard/Dashboard.types';
import ExaminersStructure from './ExaminersStructure.component';
import { useExaminersTree } from './useExaminersTree';
import { ExaminersTreeContext } from './ExaminersTreeContext';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NewMessageModal } from '../NewMessage/NewMessageModal.component';
import EnableExaminer from '../../pages/ExaminerTreemap/Actions/EnableExaminer.component';
import { EnableExaminers } from '../../pages/ExaminerTreemap/Actions/EnableExaminers.component';
import { useLayoutContext } from '../../Context/Layout.context';
import { disableExaminer, disableOrEnableExaminingTeam } from '../../services/exams/assessmentSession/assessments.service';
import { ActionMenuItem } from '../ActionsMenu/ActionsMenu.types';
import { ExaminerFunction } from '../../pages/Assessment/Assessment.types';
import useSessionUser from '../../hooks/useSessionUser';
import {
  getAllOpenChildrenCount,
  isAutoLockIcon,
  isElementInContainer,
  isManualLockIcon,
  reduceExaminersList,
} from './helpers';
import CircularProgress from '@material-ui/core/CircularProgress';
import _ from 'lodash';

interface ExaminersTreeProps {
  structure: ExaminersList | null;
  search: string;
  isFirst?: boolean;
  isControlProcessActive?: boolean;
  onActiveElementChange: (element: ExaminersList) => void;
  onClosedElementChange: (element: ExaminersList, isOpen?: boolean) => void;
  onReload: () => void;
  assessmentSession: DashboardTile | null;
  blockadeFilterType: BlockadeTypeId;
}

const structureItemsPagination = 30;

export const ExaminersTree = ({
  structure,
  search,
  isControlProcessActive,
  onActiveElementChange,
  onClosedElementChange,
  onReload,
  assessmentSession,
  blockadeFilterType,
}: ExaminersTreeProps) => {
  const [selectedExaminer, setSelectedExaminer] = useState<ExaminersList | null>(null);
  const [messageModal, setMessageModal] = useState<{ open: boolean; type: 'standard' | 'helpdesk' }>({
    open: false,
    type: 'standard',
  });
  const [examinerForEnableExaminerPopup, setExaminerForEnableExaminerPopup] = useState<{
    listElement: ExaminersList;
    isExaminingTeamChairmen: boolean;
  } | null>(null);
  const [enableExaminersModal, setEnableExaminersModal] = useState<{
    listElement: ExaminersList;
    all: boolean;
  } | null>(null);
  const [structureExtended, setStructureExtended] = useState<ExtendedExaminersList | null>(null);

  const { extendExaminersList } = useExaminersTree(search);
  const { openAlert, showLoader } = useLayoutContext();
  const { userAssessmentSessionInfo, isUserKGZ, isUserKK, isUserPZE, isSessionLoaded } = useSessionUser();
  const [renderStructureItemsCount, setRenderStructureItemsCount] = useState(structureItemsPagination);
  const allStructureItemsCount = useRef(0);
  const needToLoadMoreItemsContainerRef = useRef<HTMLDivElement | null>(null);
  const needToLoadMoreItemsLoaderRef = useRef<HTMLDivElement | null>(null);

  const userAssessmentSessionInfoFunction = userAssessmentSessionInfo.function;

  const handleScroll = useCallback(
    _.debounce(() => {
      if (
        needToLoadMoreItemsLoaderRef.current &&
        needToLoadMoreItemsContainerRef.current &&
        isElementInContainer(needToLoadMoreItemsLoaderRef.current, needToLoadMoreItemsContainerRef.current)
      ) {
        setRenderStructureItemsCount((prevValue) =>
          prevValue < allStructureItemsCount.current
            ? prevValue + structureItemsPagination
            : allStructureItemsCount.current,
        );
      }
    }, 500),
    [],
  );

  useEffect(() => {
    if (!isSessionLoaded || !structure) {
      return;
    }

    allStructureItemsCount.current = !structure.isClosed ? getAllOpenChildrenCount(structure.inferiors) : 0;
    const newStructure = extendExaminersList(
      reduceExaminersList(structure, renderStructureItemsCount, allStructureItemsCount.current),
    );
    setStructureExtended(newStructure);
  }, [isSessionLoaded, extendExaminersList, structure, renderStructureItemsCount]);

  useEffect(() => {
    if (!search) {
      setRenderStructureItemsCount(structureItemsPagination);
    }
  }, [search]);

  const selectExaminer = (examiner: ExaminersList) => setSelectedExaminer(examiner);
  const openMessageModal = () => setMessageModal({ open: true, type: 'standard' });
  const openEnableExaminerPopupForExaminer = (examiner: ExaminersList, isExaminingTeamChairmen: boolean) =>
    setExaminerForEnableExaminerPopup({ listElement: examiner, isExaminingTeamChairmen });
  const openEnableMultipleExaminersPopupForExaminer = (examiner: ExaminersList, all: boolean) =>
    setEnableExaminersModal({ listElement: examiner, all });

  const disableSelectedExaminer = useCallback(
    (examiner: ExaminersList) => {
      const isDeskAssignedExaminer = examiner?.examiner;
      const examinerPermissionId = isDeskAssignedExaminer?.examinerPermission?.id;
      if (examinerPermissionId && !isDeskAssignedExaminer?.examinerPermission?.isBlocked) {
        showLoader(true);
        disableExaminer(examinerPermissionId)
          .then(() => {
            openAlert('success');
            onReload();
          })
          .catch((error) => {
            openAlert('error');
            console.log(error);
          })
          .finally(() => {
            showLoader(false);
          });
      } else return;
    },
    [onReload, openAlert, showLoader],
  );

    const disableSelectedExaminingTeam = useCallback(
      (examiner: ExaminersList, block?: boolean) => {
        const examiningTeamId = examiner.examiningTeamId;
        if (examiningTeamId) {
          disableOrEnableExaminingTeam(examiningTeamId, block)
            .then(onReload)
            .catch((error) => {
              console.log(error);
            });
        }
      },
      [isUserKK, isUserPZE, onReload],
    );

  const handleEnableSelectedExaminer = useCallback((examiner: ExtendedExaminersList, examinerType: 'E' | 'PZE') => {
    openEnableExaminerPopupForExaminer(examiner, examinerType === 'PZE');
  }, []);

  const getMenuItemsConditions = useCallback(
    (examiner: ExtendedExaminersList | null): MenuItemsConditions => {
      const isMenuItemPZE = examiner?.function === ExaminerFunction.PZE;
      const isMenuItemCZE = examiner?.function === ExaminerFunction.CZE;

      return {
        blockUnblockTeam: (isUserPZE || isUserKGZ || isUserKK) && isMenuItemPZE,
        blockUnblockExaminer: (isUserPZE || isUserKGZ || isUserKK) && isMenuItemCZE,
        blockUnblockPZE: (isUserKGZ || isUserKK) && isMenuItemPZE && !examiner?.isQualified,
        unblockAllExaminers: !!(
          examiner?.examiner?.examinerPermission &&
          examiner?.function < ExaminerFunction.CZE &&
          examiner?.function >= userAssessmentSessionInfoFunction
        ),
        sendMessages: !!examiner?.canUserSendMessageTo,
      };
    },
    [isUserKGZ, isUserKK, isUserPZE, userAssessmentSessionInfoFunction],
  );

  const getMenuItems = useCallback(
    (
      examiner: ExtendedExaminersList,
      menuItemsConditions: MenuItemsConditions,
      isExaminingTeamBlocked: boolean,
    ): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [];

      if (!menuItemsConditions) {
        return items;
      }

      if (menuItemsConditions.blockUnblockTeam && !isExaminingTeamBlocked && isUserPZE) {
        items.push({
          text: 'Zablokuj wszystkich podległych egzaminatorów',
          onClick: () => disableSelectedExaminingTeam(examiner, true),
        });
      }
      if(isExaminingTeamBlocked && menuItemsConditions.blockUnblockTeam){
         items.push({
          text: 'Odblokuj wszystkich podległych egzaminatorów',
          onClick: () => disableSelectedExaminingTeam(examiner, false),
        });
      }

      if (menuItemsConditions.blockUnblockExaminer) {
        items.push({
          text:
            !isManualLockIcon(examiner) && !isAutoLockIcon(examiner)
              ? 'Zablokuj egzaminatora'
              : 'Odblokuj egzaminatora',
          onClick: () =>
            !isManualLockIcon(examiner) && !isAutoLockIcon(examiner)
              ? disableSelectedExaminer(examiner)
              : handleEnableSelectedExaminer(examiner, 'E'),
        });
      }

      if (menuItemsConditions.blockUnblockPZE) {
        items.push({
          text: !isManualLockIcon(examiner) && !isAutoLockIcon(examiner) ? 'Zablokuj PZE' : 'Odblokuj PZE',
          onClick: () =>
            !isManualLockIcon(examiner) && !isAutoLockIcon(examiner)
              ? disableSelectedExaminer(examiner)
              : handleEnableSelectedExaminer(examiner, 'PZE'),
        });
      }
      if (menuItemsConditions.unblockAllExaminers && !menuItemsConditions.blockUnblockTeam) {
        items.push({
          text: 'Odblokuj wszystkich egzaminatorów',
          onClick: () => {
            openEnableMultipleExaminersPopupForExaminer(examiner, true);
          },
        });

        if (blockadeFilterType !== BlockadeTypeId.None || search) {
          items.push({
            text: 'Odblokuj wyszukanych podległych egzaminatorów',
            onClick: () => {
              openEnableMultipleExaminersPopupForExaminer(examiner, false);
            },
          });
        }
      }

      if (menuItemsConditions.sendMessages) {
        items.push({
          text: 'Wyślij wiadomość',
          onClick: () => {
            openMessageModal();
            selectExaminer(examiner);
          },
        });
      }

      return items;
    },
    [blockadeFilterType, disableSelectedExaminer, disableSelectedExaminingTeam, handleEnableSelectedExaminer, search],
  );

  const handleClosedElementChange = useCallback(
    (element: ExaminersList, isOpen?: boolean) => {
      onClosedElementChange(element, isOpen);
      handleScroll();
    },
    [handleScroll, onClosedElementChange],
  );

  const contextValue = useMemo(
    () => ({
      getMenuItems,
      getMenuItemsConditions,
      onActiveElementChange,
      onClosedElementChange: handleClosedElementChange,
    }),
    [getMenuItems, getMenuItemsConditions, onActiveElementChange, handleClosedElementChange],
  );

  if (!isSessionLoaded) {
    return null;
  }

  return (
    <div
      style={{
        height: '100%',
        overflowY: 'auto',
        paddingRight: '10px',
      }}
      onScroll={handleScroll}
      ref={needToLoadMoreItemsContainerRef}
    >
      <ExaminersTreeContext.Provider value={contextValue}>
        <ExaminersStructure element={structureExtended} isFirst={true} />
      </ExaminersTreeContext.Provider>

      {allStructureItemsCount.current > renderStructureItemsCount && (
        <div ref={needToLoadMoreItemsLoaderRef}>
          <CircularProgress size={20} onClick={handleScroll} />
        </div>
      )}

      {examinerForEnableExaminerPopup?.listElement && (
        <EnableExaminer
          onSuccess={() => {
            onReload();
            setExaminerForEnableExaminerPopup(null);
          }}
          onFailure={() => {
            setExaminerForEnableExaminerPopup(null);
          }}
          isQualified={
            (examinerForEnableExaminerPopup.listElement && examinerForEnableExaminerPopup.listElement?.isQualified) ??
            false
          }
          examinerPermissionId={examinerForEnableExaminerPopup.listElement.examiner?.examinerPermission?.id ?? ''}
          examinerId={examinerForEnableExaminerPopup.listElement.examiner?.id ?? ''}
          isExaminingTeamChairmen={examinerForEnableExaminerPopup.isExaminingTeamChairmen}
          lockType={
            examinerForEnableExaminerPopup.listElement.examiner?.examinerPermission?.isBlocked ?? BlockadeTypeId.Manual
          }
          isControlProcessActive={isControlProcessActive}
          assessmentSessionId={assessmentSession?.assessmentSessionId}
        />
      )}

      <NewMessageModal
        open={messageModal.open}
        type={messageModal.type}
        onClose={() => {
          setMessageModal({ open: false, type: 'standard' });
        }}
        onSend={() => setMessageModal({ open: false, type: 'standard' })}
        recipientWithFunctionId={selectedExaminer ?? undefined}
        session={assessmentSession}
      />

      {enableExaminersModal?.listElement && (
        <EnableExaminers
          onReload={onReload}
          onSuccess={() => setEnableExaminersModal(null)}
          onFailure={() => setEnableExaminersModal(null)}
          blockadeFilterType={blockadeFilterType}
          allToUnblock={enableExaminersModal.all}
        />
      )}
    </div>
  );
};
